import * as THREE from 'three';
import { Character } from '../Character';
import { NavMesh } from '../../world/NavMesh';

const WAYPOINT_RADIUS = 1.5;
const STUCK_SAMPLE_INTERVAL = 0.5;
const STUCK_MIN_DISTANCE = 0.5;
const STUCK_TIMEOUT = 1.0;
const ESCAPE_DURATION = 1.5;

type AgentPhase = 'following' | 'escaping';

export class NavMeshAgent {
    public character: Character;
    public reverse = false;

    private waypoints: THREE.Vector3[] = [];
    private waypointIndex = 0;
    private stuckTimer = 0;
    private positionSampleTimer = 0;
    private lastSampledPosition: THREE.Vector3 | null = null;
    private phase: AgentPhase = 'following';
    private escapeTimer = 0;
    private escapeDirection: THREE.Vector3 | null = null;

    get currentTargetPosition(): THREE.Vector3 | null {
        return this.waypoints[this.waypointIndex] ?? null;
    }

    constructor(private readonly navMesh: NavMesh) {}

    public update(timeStep: number): void {
        if (!this.character || this.navMesh.triangles.length === 0) return;

        if (this.phase === 'escaping') {
            this.updateEscape(timeStep);
            return;
        }

        if (this.waypoints.length === 0) this.computePath();
        this.followWaypoints();
        this.checkStuck(timeStep);
    }

    private computePath(): void {
        const pos = this.character.getWorldPosition(new THREE.Vector3());
        const from = this.navMesh.findNearestTriangle(pos);
        const to = this.navMesh.getRandomTriangle();
        const triPath = this.navMesh.findPath(from, to);
        this.waypoints = triPath.map(i => this.navMesh.triangles[i].centroid.clone());
        this.waypointIndex = 0;
        this.reverse = false;
        this.stuckTimer = 0;
        this.positionSampleTimer = 0;
        this.lastSampledPosition = null;
    }

    private followWaypoints(): void {
        const target = this.waypoints[this.waypointIndex];
        if (!target) return;

        const pos = this.character.getWorldPosition(new THREE.Vector3());
        const toTarget = new THREE.Vector3().subVectors(target, pos);
        toTarget.y = 0;
        const dist = toTarget.length();

        if (dist > 0.1) {
            this.character.setViewVector(toTarget.normalize());
            this.character.setOrientation(toTarget, false);
        }
        this.character.triggerAction('up', true);

        if (dist < WAYPOINT_RADIUS) {
            this.advanceWaypoint();
        }
    }

    private advanceWaypoint(): void {
        if (!this.reverse) {
            if (this.waypointIndex < this.waypoints.length - 1) {
                this.waypointIndex++;
            } else {
                this.reverse = true;
                if (this.waypointIndex > 0) this.waypointIndex--;
            }
        } else {
            if (this.waypointIndex > 0) {
                this.waypointIndex--;
            } else {
                this.waypoints = [];
            }
        }
    }

    private checkStuck(timeStep: number): void {
        if (this.waypoints.length === 0) return;

        this.positionSampleTimer += timeStep;
        if (this.positionSampleTimer < STUCK_SAMPLE_INTERVAL) return;

        const currentPos = this.character.getWorldPosition(new THREE.Vector3());
        this.positionSampleTimer = 0;

        if (this.lastSampledPosition !== null) {
            const moved = currentPos.distanceTo(this.lastSampledPosition);
            if (moved < STUCK_MIN_DISTANCE) {
                this.stuckTimer += STUCK_SAMPLE_INTERVAL;
                if (this.stuckTimer >= STUCK_TIMEOUT) {
                    this.startEscape();
                    return;
                }
            } else {
                this.stuckTimer = 0;
            }
        }

        this.lastSampledPosition = currentPos;
    }

    // Picks a random horizontal direction and walks in it for ESCAPE_DURATION seconds
    // before computing a new path. This physically relocates the NPC away from the
    // blocked corridor so the next path doesn't immediately lead back into it.
    private startEscape(): void {
        this.phase = 'escaping';
        this.escapeTimer = 0;
        this.waypoints = [];
        const angle = Math.random() * Math.PI * 2;
        this.escapeDirection = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle));
    }

    private updateEscape(timeStep: number): void {
        this.character.setViewVector(this.escapeDirection);
        this.character.setOrientation(this.escapeDirection, false);
        this.character.triggerAction('up', true);

        this.escapeTimer += timeStep;
        if (this.escapeTimer >= ESCAPE_DURATION) {
            this.phase = 'following';
            this.escapeDirection = null;
        }
    }
}
